import { createSlice, PayloadAction } from '@reduxjs/toolkit';

interface NotificationState {
  id: string;
  type: 'success' | 'error' | 'warning' | 'info';
  title: string;
  message: string;
  duration?: number;
  timestamp: number;
}

interface ModalState {
  isOpen: boolean;
  type: 'confirmation' | 'form' | 'info' | 'image';
  title?: string;
  content?: any;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  onConfirm?: () => void;
  onCancel?: () => void;
}

interface LoadingState {
  [key: string]: boolean;
}

interface UIState {
  // Sidebar
  sidebarOpen: boolean;
  sidebarCollapsed: boolean;
  
  // Notifications
  notifications: NotificationState[];
  
  // Modals
  modal: ModalState;
  
  // Loading states
  loading: LoadingState;
  
  // Theme
  theme: 'light' | 'dark';
  
  // Layout
  pageTitle: string;
  breadcrumbs: Array<{
    label: string;
    href?: string;
  }>;
  
  // Search
  globalSearch: {
    query: string;
    results: any[];
    isSearching: boolean;
    isOpen: boolean;
  };
  
  // Filters
  filters: {
    [key: string]: any;
  };
  
  // View preferences
  viewPreferences: {
    tablePageSize: number;
    dateFormat: string;
    timeFormat: '12h' | '24h';
    language: 'en' | 'id';
  };
}

const initialState: UIState = {
  sidebarOpen: true,
  sidebarCollapsed: false,
  notifications: [],
  modal: {
    isOpen: false,
    type: 'info',
  },
  loading: {},
  theme: 'light',
  pageTitle: '',
  breadcrumbs: [],
  globalSearch: {
    query: '',
    results: [],
    isSearching: false,
    isOpen: false,
  },
  filters: {},
  viewPreferences: {
    tablePageSize: 15,
    dateFormat: 'DD/MM/YYYY',
    timeFormat: '24h',
    language: 'en',
  },
};

const uiSlice = createSlice({
  name: 'ui',
  initialState,
  reducers: {
    // Sidebar
    toggleSidebar: (state) => {
      state.sidebarOpen = !state.sidebarOpen;
    },
    setSidebarOpen: (state, action: PayloadAction<boolean>) => {
      state.sidebarOpen = action.payload;
    },
    toggleSidebarCollapsed: (state) => {
      state.sidebarCollapsed = !state.sidebarCollapsed;
    },
    setSidebarCollapsed: (state, action: PayloadAction<boolean>) => {
      state.sidebarCollapsed = action.payload;
    },

    // Notifications
    addNotification: (state, action: PayloadAction<Omit<NotificationState, 'id' | 'timestamp'>>) => {
      const notification: NotificationState = {
        ...action.payload,
        id: Math.random().toString(36).substr(2, 9),
        timestamp: Date.now(),
      };
      state.notifications.push(notification);
    },
    removeNotification: (state, action: PayloadAction<string>) => {
      state.notifications = state.notifications.filter(n => n.id !== action.payload);
    },
    clearNotifications: (state) => {
      state.notifications = [];
    },

    // Modals
    openModal: (state, action: PayloadAction<Omit<ModalState, 'isOpen'>>) => {
      state.modal = {
        ...action.payload,
        isOpen: true,
      };
    },
    closeModal: (state) => {
      state.modal = {
        isOpen: false,
        type: 'info',
      };
    },

    // Loading states
    setLoading: (state, action: PayloadAction<{ key: string; isLoading: boolean }>) => {
      state.loading[action.payload.key] = action.payload.isLoading;
    },
    clearLoading: (state, action: PayloadAction<string>) => {
      delete state.loading[action.payload];
    },
    clearAllLoading: (state) => {
      state.loading = {};
    },

    // Theme
    setTheme: (state, action: PayloadAction<'light' | 'dark'>) => {
      state.theme = action.payload;
    },
    toggleTheme: (state) => {
      state.theme = state.theme === 'light' ? 'dark' : 'light';
    },

    // Page metadata
    setPageTitle: (state, action: PayloadAction<string>) => {
      state.pageTitle = action.payload;
    },
    setBreadcrumbs: (state, action: PayloadAction<Array<{ label: string; href?: string }>>) => {
      state.breadcrumbs = action.payload;
    },

    // Global search
    setGlobalSearchQuery: (state, action: PayloadAction<string>) => {
      state.globalSearch.query = action.payload;
    },
    setGlobalSearchResults: (state, action: PayloadAction<any[]>) => {
      state.globalSearch.results = action.payload;
      state.globalSearch.isSearching = false;
    },
    setGlobalSearching: (state, action: PayloadAction<boolean>) => {
      state.globalSearch.isSearching = action.payload;
    },
    setGlobalSearchOpen: (state, action: PayloadAction<boolean>) => {
      state.globalSearch.isOpen = action.payload;
    },
    clearGlobalSearch: (state) => {
      state.globalSearch = {
        query: '',
        results: [],
        isSearching: false,
        isOpen: false,
      };
    },

    // Filters
    setFilter: (state, action: PayloadAction<{ key: string; value: any }>) => {
      state.filters[action.payload.key] = action.payload.value;
    },
    removeFilter: (state, action: PayloadAction<string>) => {
      delete state.filters[action.payload];
    },
    clearFilters: (state) => {
      state.filters = {};
    },

    // View preferences
    setViewPreference: (state, action: PayloadAction<{ key: keyof UIState['viewPreferences']; value: UIState['viewPreferences'][keyof UIState['viewPreferences']] }>) => {
      (state.viewPreferences as any)[action.payload.key] = action.payload.value;
    },
    setViewPreferences: (state, action: PayloadAction<Partial<UIState['viewPreferences']>>) => {
      state.viewPreferences = { ...state.viewPreferences, ...action.payload };
    },

    // Batch actions
    resetUI: (state) => {
      return {
        ...initialState,
        theme: state.theme,
        viewPreferences: state.viewPreferences,
      };
    },
  },
});

export const {
  toggleSidebar,
  setSidebarOpen,
  toggleSidebarCollapsed,
  setSidebarCollapsed,
  addNotification,
  removeNotification,
  clearNotifications,
  openModal,
  closeModal,
  setLoading,
  clearLoading,
  clearAllLoading,
  setTheme,
  toggleTheme,
  setPageTitle,
  setBreadcrumbs,
  setGlobalSearchQuery,
  setGlobalSearchResults,
  setGlobalSearching,
  setGlobalSearchOpen,
  clearGlobalSearch,
  setFilter,
  removeFilter,
  clearFilters,
  setViewPreference,
  setViewPreferences,
  resetUI,
} = uiSlice.actions;

// Selectors
export const selectSidebarOpen = (state: { ui: UIState }) => state.ui.sidebarOpen;
export const selectSidebarCollapsed = (state: { ui: UIState }) => state.ui.sidebarCollapsed;
export const selectNotifications = (state: { ui: UIState }) => state.ui.notifications;
export const selectModal = (state: { ui: UIState }) => state.ui.modal;
export const selectLoading = (state: { ui: UIState }) => (key: string) => state.ui.loading[key] || false;
export const selectTheme = (state: { ui: UIState }) => state.ui.theme;
export const selectPageTitle = (state: { ui: UIState }) => state.ui.pageTitle;
export const selectBreadcrumbs = (state: { ui: UIState }) => state.ui.breadcrumbs;
export const selectGlobalSearch = (state: { ui: UIState }) => state.ui.globalSearch;
export const selectFilters = (state: { ui: UIState }) => state.ui.filters;
export const selectViewPreferences = (state: { ui: UIState }) => state.ui.viewPreferences;

// Helper action creators
export const showSuccessNotification = (message: string, title?: string) => 
  addNotification({
    type: 'success',
    title: title || 'Success',
    message,
    duration: 5000,
  });

export const showErrorNotification = (message: string, title?: string) => 
  addNotification({
    type: 'error',
    title: title || 'Error',
    message,
    duration: 8000,
  });

export const showWarningNotification = (message: string, title?: string) => 
  addNotification({
    type: 'warning',
    title: title || 'Warning',
    message,
    duration: 6000,
  });

export const showInfoNotification = (message: string, title?: string) => 
  addNotification({
    type: 'info',
    title: title || 'Information',
    message,
    duration: 5000,
  });

export const showConfirmationModal = (
  title: string,
  content: string,
  onConfirm: () => void,
  onCancel?: () => void
) => 
  openModal({
    type: 'confirmation',
    title,
    content,
    onConfirm,
    ...(onCancel && { onCancel }),
  });

export default uiSlice.reducer;